import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCompaniesQuery } from "../company/company.queries";
import { fetchCurrentUser, useCurrentUserQuery } from "./user.queries";
import { adminApiRequest } from "../api/api-request";
import { Permissions } from "../const";
import { config } from "../config";
import {
  getCompanyFromLocalStorage,
  setCompanyToLocalStorage,
} from "../helper";

type User = {
  permissions: string[];
  id: number;
  email: string;
  createdAt: Date;
  active: boolean;
  firstName: string | null;
  lastName: string | null;
  language: string;
  fullName: string;
};
interface UserContextProps {
  user?: User;
  resetContext: () => void;
}
export const UserContext = React.createContext<UserContextProps | undefined>(
  undefined,
);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("UserContext must be used within userContextProvider");
  }
  return context;
};

const permissionBasedRoute = {
  [Permissions.GRAPHIC_VIEW]: "graphics",
  [Permissions.ALARMS_VIEW]: "alarms",
  [Permissions.HISTORY_VIEW]: "history",
  [Permissions.ENERGYREPORTS_VIEW]: "energy",
  [Permissions.POINTS_VIEW]: "points",
  [Permissions.EVENTLOG_VIEW]: "logs/event",
  [Permissions.ALARMLOG_VIEW]: "logs/alarm",
  [Permissions.CONNECTIONLOG_VIEW]: "logs/connection",
  [Permissions.DEVICES_VIEW]: "devices",
  [Permissions.CALENDARS_VIEW]: "schedule/calendars",
  [Permissions.ANALYTICS]: "schedule/timeschedules",
};

const checkPermission = (permissions, companyId) => {
  for (const [perm, route] of Object.entries(permissionBasedRoute)) {
    if (
      window.location.pathname.includes(route) &&
      permissions.includes(perm)
    ) {
      return window.location.pathname + window.location.search;
    }
  }
  return `/company/${companyId}/overview${window.location.search}`;
};

export const UserContextProvider = ({ children }) => {
  useCurrentUserQuery();
  const [currentUser, setCurrentUser] = useState<User>();
  const params = useParams();
  const { data: companies, isFetched } = useCompaniesQuery();
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isFetched) {
        return;
      }

      if (params.companyId) {
        await adminApiRequest.refreshAccessToken();
        const user = await fetchCurrentUser();

        if (cancelled) {
          return;
        }

        setCurrentUser(user);
        setCompanyToLocalStorage(params.companyId);
        const path = checkPermission(user.permissions, params.companyId);
        navigate(path, { replace: true });
      } else {
        if (companies && companies.length > 0) {
          const id = getCompanyFromLocalStorage();
          const isValidId =
            id && companies.find((company) => company.id === +id);
          const companyId = isValidId ? id : companies[0].id;
          navigate(`/company/${companyId}`, { replace: true });
        } else {
          window.location.href = `${config.ADMIN_URL}/apps?redirectUrl=${config.BASE_URL}`;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, params.companyId, isFetched]);

  const resetContext = () => {
    if (currentUser) {
      setCurrentUser({ ...currentUser, permissions: [] });
    }
  };

  const value = useMemo(() => {
    return { user: currentUser, resetContext };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
